import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('farmer' | 'insurer' | 'admin' | 'urban_manager' | 'cooperative' | 'pilot_user')[];
  redirectTo?: string;
}

const ProtectedRoute = ({ 
  children, 
  allowedRoles = ['farmer', 'insurer', 'admin', 'urban_manager', 'cooperative', 'pilot_user'], 
  redirectTo 
}: ProtectedRouteProps) => {
  const { profile, loading, userRole, isCooperative } = useUserRole();
  const location = useLocation();

  // Debug logs para identificar problemas
  useEffect(() => {
    console.log('🛡️ ProtectedRoute check:', { 
      loading, 
      hasProfile: !!profile, 
      userRole, 
      allowedRoles,
      pathname: location.pathname,
      isCooperative
    });
  }, [loading, profile, userRole, allowedRoles, location.pathname, isCooperative]);

  // Mientras carga, mostrar spinner
  if (loading) {
    console.log('⏳ ProtectedRoute: Still loading...');
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Si no hay perfil, redirigir a login
  if (!profile) {
    console.log('❌ ProtectedRoute: No profile found, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Validar rol si se especificaron roles permitidos
  if (allowedRoles && allowedRoles.length > 0 && userRole) {
    // Normalizar a minúsculas para evitar problemas de case-sensitivity
    const normalizedUserRole = userRole.toLowerCase();
    const normalizedAllowedRoles = allowedRoles.map(role => role.toLowerCase());
    
    // pilot_user tiene acceso completo a todas las rutas (similar a admin)
    if (normalizedUserRole === 'pilot_user') {
      console.log('✅ ProtectedRoute: Pilot user has full access');
      return <>{children}</>;
    }
    
    // Si el usuario no tiene un rol permitido
    if (!normalizedAllowedRoles.includes(normalizedUserRole)) {
      console.log('❌ ProtectedRoute: Role not allowed', { 
        userRole: normalizedUserRole, 
        allowedRoles: normalizedAllowedRoles 
      });
      return <Navigate to={redirectTo || '/dashboard'} replace />;
    }
  }

  // Si no hay rol definido pero se requieren roles específicos, bloquear
  if (allowedRoles && allowedRoles.length > 0 && !userRole) {
    console.log('⚠️ ProtectedRoute: No role found but roles required, redirecting');
    return <Navigate to={redirectTo || '/dashboard'} replace />;
  }

  // Acceso concedido
  console.log('✅ ProtectedRoute: Access granted');
  return <>{children}</>;
};

export default ProtectedRoute;
